<template>
  <v-tooltip location="top" open-delay="500" :disabled="!hasValidName">
    <template v-slot:activator="{ props }">
      <v-avatar
        :style="{
          border: '1px solid ' + finalColor,
          color: finalColor
        }"
        :size="large ? 24 : 20"
        class="avatar large mx-1 show-on-hover-item"
        v-bind="props">

        <img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" style="width: 100%; height: 100%; object-fit: cover;" />

        <v-icon
          v-else-if="generatedByAi"
          :size="large ? 'default' : 'small'"
          :color="finalColor"
          style="margin: 1px 2px 0 0;"
        >
          mds:temp-preferences-custom
        </v-icon>
        <v-icon
          v-else-if="initials === '*'"
          :size="large ? 'default' : 'small'"
          :color="finalColor"
          style="margin: 0;"
        >
          mds:person
        </v-icon>
        <span v-else class="initials">{{ initials }}</span>
      </v-avatar>
    </template>

    <div>
      <p class="text-body-2 mb-0">{{ (generatedByAi ? 'Generated by AI by ' : '') + fullname }}</p>
    </div>
  </v-tooltip>
</template>

<script>
import Storage from '@/storage'
import { isDarkTheme } from '@/composables/useAppTheme'

const storageHelper = new Storage()

export default {
  props: ['fullname', 'large', 'grayscale', 'generatedByAi', 'picture'],

  data () {
    return {
      avatarUrl: null
    }
  },

  computed: {
    initials () {
      const { fullname } = this

      if (typeof fullname !== 'string' || !fullname.trim().length) {
        return '*'
      }

      const [first, last] = this.fullname.split(' ')

      const f = first.charAt(0)
      const l = typeof last === 'string' ? last.charAt(0) : ''

      return f + l
    },

    initialsColor () {
      const str = this.initials
      let hash = 0x811C9DC5
      for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i) - 65
        hash = Math.imul(hash, 0x010000193)
        hash ^= hash >>> 7
      }
      hash = Math.abs(hash)

      const h = hash % 360
      const s = 75 + (hash % 10)
      const l = (isDarkTheme() ? 60 : 25) + (hash % 20)
      const color = `hsl(${h}, ${s}%, ${l}%)`
      return color
    },

    finalColor () {
      if (this.grayscale || this.initials === '*') {
        return '#878F99'
      }

      return this.initialsColor
    },

    hasValidName () {
      return typeof this.fullname === 'string' && this.fullname.trim().length > 0
    }
  },

  watch: {
    picture: {
      handler (newPicture) {
        this.resolveAvatarUrl(newPicture)
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    resolveAvatarUrl (pictureData) {
      if (!pictureData) {
        this.avatarUrl = null
        return
      }

      if (pictureData.file?.key) {
        const cloudFrontUrl = storageHelper.getAvatarCloudFrontUrl(pictureData.file.key)

        if (cloudFrontUrl) {
          this.avatarUrl = cloudFrontUrl
          return
        }
      }

      if (pictureData.url) {
        try {
          const urlObj = new URL(pictureData.url)
          const hostname = urlObj.hostname
          if (
            hostname === 'cloudfront.net' ||
            hostname.endsWith('.cloudfront.net')
          ) {
            this.avatarUrl = pictureData.url
            return
          }
        } catch (e) {
          // Invalid URL; skip to next block
        }
      }

      if (pictureData.url && pictureData.url.includes('X-Amz')) {
        const extractedKey = storageHelper.extractKeyFromPresignedUrl(pictureData.url)

        if (extractedKey) {
          const cloudFrontUrl = storageHelper.getAvatarCloudFrontUrl(extractedKey)

          if (cloudFrontUrl) {
            this.avatarUrl = cloudFrontUrl
            return
          }
        }

        this.avatarUrl = null
        return
      }

      this.avatarUrl = pictureData.url || null
    }
  }
}
</script>

<style scoped>
.avatar {
  flex-shrink: 0;
  background: none !important;
}

.initials {
  text-transform: uppercase;
  text-align: right;
  font-size: 10px;
  font-weight: 500;
  line-height: 110%;
  letter-spacing: -0.3px;
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--color-primary);
  border-radius: 10px;
}
</style>
